import { BaseAPI } from 'API/BaseApi';
import { ISigningTypeResponse } from '../../../types/signingAppointment/ISigningTypeResponse';
import { ISigningMethodResponse } from '../../../types/signingAppointment/ISigningMethodResponse';
import { eAPITags } from '../../eAPITags';
import { IDeleteSignerParams } from '../../../types/signingAppointment/IDeleteSignerParams';
import { ISigningAppointmentStatusResponse } from '../../../types/signingAppointment/ISigningAppointmentStatusResponse';
import {
  ISigningAppointmentResponse,
  sortSigningAppointments,
} from '../../../types/signingAppointment/ISigningAppointmentResponse';
import { CreateSigningAppointmentRequest } from '../../../types/signingAppointment/CreateSigningAppointmentRequest';
import { IEditSigningAppointmentRequest } from '../../../types/signingAppointment/IEditSigningAppointmentRequest';
import {
  ICreateSigningAppointmentDocumentRequest,
  ISendAllDocumentsRequest,
  ISigningAppointmentDocumentResponse,
  IUpdateSigningAppointmentDocumentRequest,
} from '../../../types/signingAppointment/ISigningAppointmentDocumentResponse';
import { ISigSyncProgressionLine } from '../../../types/signingAppointment/ISigSyncProgressionLine';
import { ISigSyncEventType } from '../../../types/signingAppointment/ISigSyncEventType';
import { UUID } from '../../../types/shared/UUID';
import { convertFromIQueryResponseToIPageData, IPageData } from '../../../types/rest/IPageData';
import { ISigningAppointmentSearchRequest } from '../../../types/signingAppointment/ISigningAppointmentSearchRequest';
import { ISigningAppointmentDashboardResponse } from '../../../types/signingAppointment/ISigningAppointmentDashboardResponse';
import { IQueryResponse } from '../../../types/rest/IQueryResponse';
import { labelComparator } from '../../../utilities/labelComparator';

/**
 * Signing Appointment Timber API
 */
export const SigningAppointmentApi = BaseAPI.injectEndpoints({
  endpoints: (build) => ({
    create: build.mutation<
      ISigningAppointmentResponse,
      {
        orderId: string;
        data: CreateSigningAppointmentRequest;
      }
    >({
      query: ({ orderId, data }) => ({
        url: `/timber/api/internal/orders/${orderId}/signing-appointments`,
        method: 'POST',
        body: data,
      }),
      transformResponse: (response: ISigningAppointmentResponse) => response,
      invalidatesTags: (response, error, { orderId }) => [{ type: eAPITags.SigningAppointmentListData, id: orderId }],
    }),
    update: build.mutation<
      ISigningAppointmentResponse,
      { id: string; orderId: string; data: IEditSigningAppointmentRequest }
    >({
      query: ({ id, orderId, data }) => ({
        url: `/timber/api/internal/orders/${orderId}/signing-appointments/${id}`,
        method: 'PATCH',
        body: data,
      }),
      invalidatesTags: (response, error, { id, orderId }) => [
        { type: eAPITags.SigningAppointmentData, id: id },
        { type: eAPITags.SigningAppointmentListData, id: orderId },
        { type: eAPITags.SigningAppointmentProgressionsData, id: id },
      ],
    }),
    listSigningAppointments: build.query<ISigningAppointmentResponse[], { orderId: string }>({
      query: ({ orderId }) => ({
        url: `/timber/api/internal/orders/${orderId}/signing-appointments`,
        method: 'GET',
      }),
      transformResponse: (baseReturnValue: ISigningAppointmentResponse[]) => sortSigningAppointments(baseReturnValue),
      providesTags: (response, error, params) => [
        {
          type: eAPITags.SigningAppointmentListData,
          id: params.orderId,
        },
      ],
    }),
    get: build.query<ISigningAppointmentResponse, { id: string; orderId: string }>({
      query: ({ id, orderId }) => ({
        url: `/timber/api/internal/orders/${orderId}/signing-appointments/${id}`,
        method: 'GET',
      }),
      providesTags: (response, error, params) => [{ type: eAPITags.SigningAppointmentData, id: params.id }],
    }),
    getProgressions: build.query<
      Record<ISigSyncProgressionLine, ISigSyncEventType | null>,
      { id: string; orderId: string }
    >({
      query: ({ id, orderId }) => ({
        url: `/timber/api/internal/orders/${orderId}/signing-appointments/${id}/progressions`,
        method: 'GET',
      }),
      providesTags: (response, error, params) => [
        {
          type: eAPITags.SigningAppointmentProgressionsData,
          id: params.id,
        },
      ],
    }),
    listStatuses: build.query<ISigningAppointmentStatusResponse[], void>({
      query: () => ({
        url: `/timber/api/internal/signing-appointment-statuses`,
        method: 'GET',
        providesTags: [{ type: eAPITags.SigningAppointmentStatusData, id: 'STATUSES' }],
      }),
    }),
    listTypes: build.query<ISigningTypeResponse[], void>({
      query: () => ({
        url: `/timber/api/internal/signing-types`,
        method: 'GET',
        transformResponse: (baseReturnValue: ISigningTypeResponse[]) => baseReturnValue.toSorted(labelComparator),
        providesTags: [{ type: eAPITags.SigningAppointmentTypeData, id: 'TYPES' }],
      }),
    }),
    listMethods: build.query<ISigningMethodResponse[], void>({
      query: () => ({
        url: `/timber/api/internal/signing-methods`,
        method: 'GET',
        transformResponse: (baseReturnValue: ISigningMethodResponse[]) => baseReturnValue.toSorted(labelComparator),
        providesTags: [{ type: eAPITags.SigningAppointmentMethodData, id: 'METHODS' }],
      }),
    }),
    deleteSigner: build.mutation<void, IDeleteSignerParams>({
      query: ({ id, orderId, signerId }) => ({
        url: `/timber/api/internal/orders/${orderId}/signing-appointments/${id}/signers/${signerId}`,
        method: 'DELETE',
      }),
      invalidatesTags: (response, error, { id, orderId }) => [
        {
          type: eAPITags.SigningAppointmentData,
          id: id,
        },
        { type: eAPITags.SigningAppointmentListData, id: orderId },
      ],
    }),
    createSigningAppointmentDocument: build.mutation<
      ISigningAppointmentDocumentResponse,
      ICreateSigningAppointmentDocumentRequest
    >({
      query: (body) => ({
        url: `/timber/api/internal/signing-appointments/${body.signingAppointmentId}/documents`,
        method: 'POST',
        body,
      }),
      invalidatesTags: (response, error, { orderId, signingAppointmentId }) => [
        {
          type: eAPITags.SigningAppointmentListData,
          id: orderId,
        },
        {
          type: eAPITags.SigningAppointmentData,
          id: signingAppointmentId,
        },
      ],
    }),
    updateSigningAppointmentDocument: build.mutation<
      ISigningAppointmentDocumentResponse,
      IUpdateSigningAppointmentDocumentRequest
    >({
      query: (body) => ({
        url: `/timber/api/internal/signing-appointments/${body.signingAppointmentId}/documents/${body.standardDocumentId}`,
        method: 'PATCH',
        body,
      }),
      invalidatesTags: (response, error, { orderId, signingAppointmentId }) => [
        {
          type: eAPITags.SigningAppointmentData,
          id: signingAppointmentId,
        },
        {
          type: eAPITags.SigningAppointmentListData,
          id: orderId,
        },
      ],
    }),
    sendAllDocuments: build.mutation<ISigningAppointmentDocumentResponse, ISendAllDocumentsRequest>({
      query: (body) => ({
        url: `/timber/api/internal/signing-appointments/${body.signingAppointmentId}/documents/send`,
        method: 'POST',
      }),
      invalidatesTags: (response, error, { orderId, signingAppointmentId }) => [
        {
          type: eAPITags.SigningAppointmentData,
          id: signingAppointmentId,
        },
        {
          type: eAPITags.SigningAppointmentListData,
          id: orderId,
        },
      ],
    }),
    resendMagicLinkEmail: build.mutation<void, { appointmentId: UUID; orderId: UUID }>({
      query: (body) => ({
        url: `/timber/api/internal/dss/v1/resend_invitation`,
        method: 'POST',
        params: {
          appointmentId: body.appointmentId,
        },
      }),
      invalidatesTags: (_response, _error, { orderId, appointmentId }) => [
        {
          type: eAPITags.SigningAppointmentData,
          id: appointmentId,
        },
        {
          type: eAPITags.SigningAppointmentListData,
          id: orderId,
        },
      ],
    }),
    listPagedSigningAppointmentsSearch: build.query<
      IPageData<ISigningAppointmentDashboardResponse>,
      ISigningAppointmentSearchRequest
    >({
      query: (params) => ({
        url: `/timber/api/internal/signing-appointments/search`,
        params,
      }),
      providesTags: [{ type: eAPITags.SigningAppointmentListData, id: 'LIST' }],
      transformResponse: (baseReturnValue: IQueryResponse<ISigningAppointmentDashboardResponse>) =>
        convertFromIQueryResponseToIPageData(baseReturnValue, (response) => response),
    }),
  }),
});

export const {
  useCreateMutation,
  useUpdateMutation,
  useListSigningAppointmentsQuery,
  useListStatusesQuery,
  useListTypesQuery,
  useListMethodsQuery,
  useDeleteSignerMutation,
  useCreateSigningAppointmentDocumentMutation,
  useUpdateSigningAppointmentDocumentMutation,
  useSendAllDocumentsMutation,
  useGetProgressionsQuery,
  useResendMagicLinkEmailMutation,
  useListPagedSigningAppointmentsSearchQuery,
} = SigningAppointmentApi;
